/**
 * Quantity-tier picker — click a tier to set product quantity.
 * Universal tiers (no tag/SKU rules). Active box shows “Du sparst …”.
 * Optional B2B note appears when the highest discount tier is selected.
 */
class QuantityTiers extends HTMLElement {
  connectedCallback() {
    this.buttonsWrap = this.querySelector('.em-qty-tiers__btns');
    this.buttons = Array.from(this.querySelectorAll('.em-qty-tiers__btn[data-qty]'));
    this.b2bNote = this.querySelector('[data-em-qty-b2b-note]');
    this.basePrice = Number(this.dataset.basePrice || 0);
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.savingsYouPrefix = this.dataset.savingsYouPrefix || 'Du sparst';
    this.savingsPrefix = this.dataset.savingsPrefix || 'Spare';
    this.qtyLabel = this.dataset.qtyLabel || 'Menge:';
    this.showEmoji = this.dataset.showEmoji === 'true';
    this.variants = this.parseVariants();
    this.pubsubUnsubscriber = null;

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onQtyInputChange = this.onQtyInputChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.onPubSubVariantChange = this.onPubSubVariantChange.bind(this);
    this.positionCaret = this.positionCaret.bind(this);

    this.bindButtons();

    const qtyInput = this.getQtyInput();
    if (qtyInput) {
      qtyInput.addEventListener('change', this.onQtyInputChange);
      qtyInput.addEventListener('input', this.onQtyInputChange);
    }

    document.addEventListener('variant:change', this.onVariantChange);
    document.addEventListener('change', this.onFormChange);

    if (window.theme?.pubsub?.subscribe && window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      this.pubsubUnsubscriber = window.theme.pubsub.subscribe(
        window.theme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onPubSubVariantChange
      );
    }

    window.addEventListener('resize', this.positionCaret);
    requestAnimationFrame(() => this.positionCaret());
  }

  disconnectedCallback() {
    this.unbindButtons();
    const qtyInput = this.getQtyInput();
    if (qtyInput) {
      qtyInput.removeEventListener('change', this.onQtyInputChange);
      qtyInput.removeEventListener('input', this.onQtyInputChange);
    }
    document.removeEventListener('variant:change', this.onVariantChange);
    document.removeEventListener('change', this.onFormChange);
    window.removeEventListener('resize', this.positionCaret);
    if (typeof this.pubsubUnsubscriber === 'function') {
      this.pubsubUnsubscriber();
      this.pubsubUnsubscriber = null;
    }
  }

  parseVariants() {
    const jsonEl = this.querySelector('[data-em-qty-variants]');
    if (!jsonEl) return [];
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return [];
    }
  }

  bindButtons() {
    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick));
  }

  unbindButtons() {
    this.buttons.forEach((button) => button.removeEventListener('click', this.onButtonClick));
  }

  getQtyInput() {
    const id = this.dataset.qtyInputId;
    if (id) {
      const byId = document.getElementById(id);
      if (byId) return byId;
    }

    const formId = this.dataset.productForm;
    if (formId) {
      const form = document.getElementById(formId);
      if (form) {
        return (
          form.querySelector('input[name="quantity"]') ||
          document.querySelector(`input[name="quantity"][form="${formId}"]`)
        );
      }
      return document.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    return document.querySelector('input[name="quantity"]');
  }

  getQtyField() {
    const input = this.getQtyInput();
    if (!input) return null;
    return input.closest('.quantity') || input;
  }

  positionCaret() {
    const field = this.getQtyField();
    if (!field) return;

    const fieldRect = field.getBoundingClientRect();
    const selfRect = this.getBoundingClientRect();
    if (!fieldRect.width || !selfRect.width) return;

    const centerX = fieldRect.left + fieldRect.width / 2 - selfRect.left;
    const clamped = Math.max(16, Math.min(centerX, selfRect.width - 16));
    this.style.setProperty('--em-qty-caret-left', `${Math.round(clamped)}px`);
  }

  onButtonClick(event) {
    this.selectTier(event.currentTarget, { syncInput: true });
  }

  onQtyInputChange() {
    const qtyInput = this.getQtyInput();
    if (!qtyInput) return;
    const qty = Number(qtyInput.value || 0);
    const match = this.findTierForQty(qty);
    if (match) {
      this.selectTier(match, { syncInput: false });
    } else {
      this.buttons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
        this.resetTierLabel(button);
      });
      this.toggleB2bNote(false);
    }
  }

  onPubSubVariantChange(event) {
    const variant = event?.data?.variant;
    if (variant) this.applyVariant(variant);
  }

  onVariantChange(event) {
    const variant = event?.detail?.variant;
    if (variant) this.applyVariant(variant);
  }

  onFormChange(event) {
    const target = event.target;
    if (!target || target.name !== 'id') return;

    const formId = this.dataset.productForm;
    if (formId) {
      const form = target.closest('form');
      const formEl = document.getElementById(formId);
      if (form && formEl && form !== formEl && target.getAttribute('form') !== formId) return;
    }

    const variant = this.variants.find((v) => String(v.id) === String(target.value));
    if (variant) this.applyVariant(variant);
  }

  applyVariant(variant) {
    if (!variant) return;

    if (variant.price != null) {
      this.basePrice = Number(variant.price);
      this.dataset.basePrice = String(this.basePrice);
    }

    const active = this.buttons.find((button) => button.classList.contains('is-active'));
    if (active) {
      this.selectTier(active, { syncInput: false });
    }
  }

  findTierForQty(qty) {
    const sorted = [...this.buttons].sort((a, b) => Number(a.dataset.qty) - Number(b.dataset.qty));
    let match = null;
    sorted.forEach((button) => {
      if (qty >= Number(button.dataset.qty)) match = button;
    });
    return match;
  }

  getHighestTierQty() {
    return this.buttons.reduce((max, button) => {
      const qty = Number(button.dataset.qty || 0);
      return qty > max ? qty : max;
    }, 0);
  }

  toggleB2bNote(show) {
    if (!this.b2bNote) return;
    this.b2bNote.hidden = !show;
  }

  selectTier(button, { syncInput = true } = {}) {
    if (!button) return;

    this.buttons.forEach((item) => {
      const isActive = item === button;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) {
        this.setActiveTierLabel(item);
      } else {
        this.resetTierLabel(item);
      }
    });

    const qty = Number(button.dataset.qty || 0);
    const highest = this.getHighestTierQty();
    this.toggleB2bNote(highest > 0 && qty >= highest);

    if (syncInput) {
      this.setQuantity(qty);
    }
  }

  resetTierLabel(button) {
    const label = button.querySelector('[data-tier-label]');
    if (!label) return;
    label.textContent = button.dataset.idleLabel || '';
  }

  setActiveTierLabel(button) {
    const label = button.querySelector('[data-tier-label]');
    if (!label) return;

    const qty = Number(button.dataset.qty || 0);
    const percent = Number(button.dataset.percent || 0);
    const unitDiscount = Math.round((this.basePrice * percent) / 100);
    const totalSavings = unitDiscount * qty;

    if (!totalSavings || totalSavings <= 0) {
      label.textContent = button.dataset.idleLabel || '';
      return;
    }

    const money = this.formatMoney(totalSavings);
    const emoji = this.showEmoji ? '🤑 ' : '';
    label.textContent = `${emoji}${this.savingsYouPrefix} ${money}`;
  }

  setQuantity(qty) {
    const qtyInput = this.getQtyInput();
    if (!qtyInput) return;

    const min = Number(qtyInput.min || 1);
    const max = qtyInput.max !== '' ? Number(qtyInput.max) : null;
    let next = Math.max(qty, min);
    if (max != null && !Number.isNaN(max)) next = Math.min(next, max);

    if (String(qtyInput.value) === String(next)) return;

    qtyInput.value = String(next);
    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(Number(cents) / 100));
    const amountWithComma = amount.replace('.', ',');

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }

    return this.moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }
}

if (!customElements.get('quantity-tiers')) {
  customElements.define('quantity-tiers', QuantityTiers);
}
